/**
 * useKeyboard Hook
 * Keyboard shortcuts management
 */

import { useEffect, useCallback } from "react";

interface KeyboardHandlers {
  onEscape?: () => void;
  onSlash?: () => void;
  onArrowUp?: () => void;
  onArrowDown?: () => void;
  onArrowLeft?: () => void;
  onArrowRight?: () => void;
  onEnter?: () => void;
}

export function useKeyboard(handlers: KeyboardHandlers): void {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Ignore if user is typing in an input
      const target = event.target as HTMLElement;
      const isInput =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      // Handle Escape regardless of focus
      if (event.key === "Escape" && handlers.onEscape) {
        event.preventDefault();
        handlers.onEscape();
        return;
      }

      // Skip other shortcuts if typing in input
      if (isInput) return;

      switch (event.key) {
        case "/":
          if (handlers.onSlash) {
            event.preventDefault();
            handlers.onSlash();
          }
          break;

        case "ArrowUp":
          if (handlers.onArrowUp) {
            event.preventDefault();
            handlers.onArrowUp();
          }
          break;

        case "ArrowDown":
          if (handlers.onArrowDown) {
            event.preventDefault();
            handlers.onArrowDown();
          }
          break;

        case "ArrowLeft":
          if (handlers.onArrowLeft) {
            event.preventDefault();
            handlers.onArrowLeft();
          }
          break;

        case "ArrowRight":
          if (handlers.onArrowRight) {
            event.preventDefault();
            handlers.onArrowRight();
          }
          break;

        case "Enter":
          if (handlers.onEnter) {
            // Only prevent default if we have a handler
            handlers.onEnter();
          }
          break;
      }
    },
    [handlers]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);
}

export default useKeyboard;
